// 261025 E3-P3f — thêm cột tick (chỉ tập «Đạt» đẩy được) + thanh dính «Duyệt & đẩy N tập» (đẩy theo lô, việc nền) + hộp kết quả. Quyết định ở `handoffBatchView.ts`; hook ở `useHandoffBatch.ts`.
// 261023 E3-P3a — «Sổ duyệt»: MỘT bảng cả truyện (chỉ đọc). Mỗi tập một hàng: giám khảo AI · lỗi cần xem · tên gốc rò · tình tiết chưa lên phim · Chapters.
// Mọi quyết định (nhóm, nút chính, sắp xếp, URL) ở `reviewBookView.ts` (hàm thuần, có jest); file này chỉ nối dây + vẽ. KHÔNG tự bấm/ghi gì:
// «Nhờ Trợ lý sửa lỗi» CHỈ mở tập + tick sẵn lỗi trong khung Trợ lý (không truyền autoSend/suggestedText ⇒ không tự gửi, không tốn AI).
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Checkbox, Space, Spin, Tooltip, Typography } from 'antd';
import Button from './WbButton';
import { useSearchParams } from 'react-router-dom';
import { adaptationApi } from '../../../services/api/adaptation';
import type { HandoffPlanPreview } from '../../../types/adaptationWorkbench';
import { seasonReaderCardView } from '../seasonReaderView';
import AutoFixCardView from './AutoFixCardView';
import { askConfirm } from './appendRunActions';
import HandoffBatchResults from './HandoffBatchResults';
import HandoffModal from './HandoffModal';
import {
  batchItemsOf, canTick, headerTick, partialConfirmOf, planCountsOf, pruneSelection, selectAllTickable, stickyView, tickableEpisodes, tickOffReason, toggleTick,
} from './handoffBatchView';
import { useHandoffBatchActions, useHandoffPreview } from './useHandoffBatch';
import RejudgeButton from './RejudgeButton';
import { rejudgeActionOf } from './rejudgeView';
import { friendlyReasonText } from './jobErrorText';
import {
  cycleSort, defaultFilter, emptyFilterText, filterRows, handoffUnknownReason, parseReviewFilter, reviewBookCounts, reviewBookRows, ReviewFilter, ReviewRow, setReviewFilterParams,
  SortKey, SortState, sortRows, stepRowItems, CellTone, MACHINE_SKIPPED_TAG,
} from './reviewBookView';
import { useHandoffClick } from './useHandoffClick';
import { useCtaHolds, useWorkbenchCtx } from './WorkbenchContext';
import { chainOf } from './stageNavView';
import { REVIEW_DESC, reviewFootText } from './stageCopy';
import { showReviewHeaderPush } from './wbButtonView';
import { Bang, Dot, Pill } from './WbSlot';
import LocalizeTraceNote from './LocalizeTraceNote';
import { STAGE_LABEL } from './workbenchView';
import { previewBarView, previewReadyForPlan, skipRowView, skippedFromPreview, PreviewOverlay, sourceNameSuffix } from './skipReasonView';
import { summarizeWarnings, toggleExpandedGroup, warningSummaryGroupViews, type WarnGroupKey } from './warningGroupView';

const { Text } = Typography;

const TABS: Array<{ key: ReviewFilter; label: string }> = [
  { key: 'need', label: 'Còn cần bạn' }, { key: 'hint', label: 'Gợi ý' }, { key: 'auto', label: 'Máy đã tự sửa' }, { key: 'pass', label: 'Đạt' }, { key: 'pushed', label: 'Đã đẩy' }, { key: 'all', label: 'Tất cả' },
];

const PILL_TONE: Record<CellTone, 'ok' | 'warn' | 'todo' | 'bad'> = { ok: 'ok', warn: 'warn', muted: 'todo', bad: 'bad' };

type PlanState = { status: 'loading' } | { status: 'ok'; plan: HandoffPlanPreview } | { status: 'unknown'; reason: string };

const Dash: React.FC = () => <span className="wb-rb-dash" aria-label="không có">—</span>;

const SortTh: React.FC<{ label: string; tip: string; k: SortKey; sort: SortState | null; onSort: (k: SortKey) => void }> = ({ label, tip, k, sort, onSort }) => {
  const on = sort?.key === k;
  const ariaSort = on ? (sort!.dir === 'asc' ? 'ascending' : 'descending') : 'none';
  return (
    <th className={`wb-rb-sortable${on ? ' wb-rb-on' : ''}`} aria-sort={ariaSort}>
      <button type="button" title={tip} onClick={() => onSort(k)}>
        {label} <span aria-hidden="true">{on ? (sort!.dir === 'asc' ? '▴' : '▾') : '↕'}</span>
      </button>
    </th>
  );
};

const ReviewBook: React.FC = () => {
  const { storyId, workbench, data, dataVersion, seasonReader, busy, scriptJob, stageState, goTo, requestAssistant, openEpisodeFromReview, refreshAll, setReviewTick, appendDismissedId } = useWorkbenchCtx();
  const stickyCta = useCtaHolds().holds('sticky');
  const [searchParams, setSearchParams] = useSearchParams();
  const handoff = useHandoffClick();
  const [plan, setPlan] = useState<PlanState>({ status: 'loading' });
  const [planVersion, setPlanVersion] = useState<number | null>(null);
  const [sort, setSort] = useState<SortState | null>(null);
  const [sel, setSel] = useState<ReadonlySet<number>>(() => new Set<number>());
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<ReadonlySet<WarnGroupKey>>(() => new Set());
  const tickSha = useRef<Map<number, string>>(new Map());   // băm LÚC TICK: nội dung tập đổi sau đó ⇒ bỏ tick (không đẩy bản người chưa xem)
  const scripts = data.scripts;
  const hasScripts = scripts.length > 0;

  // Vùng giữa (`.wb-scroll`) dùng chung với các chặng ⇒ mở Sổ luôn từ đầu bảng, không thừa vị trí cuộn của chặng trước.
  useEffect(() => { document.querySelector('.wb .wb-scroll')?.scrollTo({ top: 0 }); }, []);

  // Q4: GET handoff-plan CHỈ khi mở Sổ và khi dữ liệu đổi sau khi Sổ đang mở (`dataVersion`) — KHÔNG gắn vào vòng poll của workbench.
  // Q5: 409 / lỗi mạng ⇒ cột «Tên gốc rò» ghi «Chưa rõ» (xám, tooltip tiếng Việt), không hộp lỗi.
  useEffect(() => {
    if (!hasScripts) { setPlan({ status: 'unknown', reason: 'Chưa có kịch bản nào để dò tên gốc' }); return undefined; }
    let cancelled = false;
    setPlanVersion(null);
    setPlan((cur) => (cur.status === 'ok' ? cur : { status: 'loading' }));   // đang có số cũ thì giữ, khỏi nhấp nháy
    adaptationApi.handoffPlan(storyId)
      .then((p) => { if (!cancelled) { setPlan({ status: 'ok', plan: p }); setPlanVersion(dataVersion); } })
      .catch((e) => { if (!cancelled) setPlan({ status: 'unknown', reason: handoffUnknownReason(e) }); });
    return () => { cancelled = true; };
  }, [storyId, dataVersion, hasScripts]);

  const readerView = useMemo(() => seasonReaderCardView(seasonReader.resp), [seasonReader.resp]);
  const rows = useMemo<ReviewRow[]>(() => {
    if (!workbench) return [];
    return reviewBookRows({
      workbench, scripts, reader: readerView,
      handoff: plan.status === 'ok' ? plan.plan : 'unknown',
      handoffReason: plan.status === 'unknown' ? plan.reason : plan.status === 'loading' ? 'đang dò tên gốc…' : undefined,
    });
  }, [workbench, scripts, readerView, plan]);
  const counts = useMemo(() => reviewBookCounts(rows), [rows]);

  // 3f: lô đẩy mới nhất + chọn tập. Dữ liệu đổi (đã đẩy xong, tập có lỗi mới, nội dung tập đổi) ⇒ bỏ tick không còn hợp lệ.
  const batch = workbench?.handoffBatch ?? null;
  const batchRunning = batch?.status === 'RUNNING';
  const batchActions = useHandoffBatchActions(storyId, batch, refreshAll);
  // Request set stays raw: preview itself decides whether a row is now skipped.
  const rawPreviewItems = useMemo(() => batchItemsOf(workbench?.episodes ?? [], tickableEpisodes(rows)), [workbench, rows]);
  const previewState = useHandoffPreview(storyId, rawPreviewItems, dataVersion, rawPreviewItems.length > 0 && !batchRunning && previewReadyForPlan(plan.status, planVersion, dataVersion));
  const previewBar = previewBarView(previewState.status);
  const overlay = useMemo(() => previewState.status === 'ok' ? skippedFromPreview(previewState.data.episodes) : new Map<number, PreviewOverlay>(), [previewState]);
  const warningSummary = useMemo(() => summarizeWarnings(scripts, rows, overlay), [scripts, rows, overlay]);
  const summaryGroups = useMemo(() => warningSummaryGroupViews(warningSummary.groups, expandedGroups), [warningSummary.groups, expandedGroups]);
  const effectiveRows = useMemo(() => rows.map((r) => ({ ...r, ...(overlay.has(r.ep) ? { previewOverlay: overlay.get(r.ep) } : {}) })), [rows, overlay]);
  useEffect(() => {
    setSel((cur) => {
      if (cur.size === 0) return cur;
      let next = pruneSelection(cur, effectiveRows);
      next = new Set(Array.from(next).filter((ep) => tickSha.current.get(ep) === effectiveRows.find((r) => r.ep === ep)?.contentSha));
      return next.size === cur.size ? cur : next;
    });
  }, [effectiveRows]);
  const selected = useMemo(() => tickableEpisodes(effectiveRows).filter((e) => sel.has(e)), [effectiveRows, sel]);
  const selItems = useMemo(() => batchItemsOf(workbench?.episodes ?? [], selected), [workbench, selected]);
  const previewCounts = previewState.status === 'ok' ? planCountsOf(previewState.data.episodes.filter((e) => selected.includes(e.episode))) : null;
  const sticky = stickyView(sel, effectiveRows, previewCounts);
  // 3h3 (H3/H9): báo số tập đã tick cho vỏ — tick ≥ 1 thì thanh dính giữ nút đỏ, nút ở dải hạ thành viền; rời Sổ ⇒ về 0.
  useEffect(() => { setReviewTick(selected.length); return () => setReviewTick(0); }, [selected.length, setReviewTick]);
  // K11: dải chuỗi đang giữ việc đẩy (chuỗi DONE chưa đóng) ⇒ ẩn «Đẩy sang Chapters…» ở header; truyện không có dải ⇒ hiện, trung tính.
  const bandHoldsPush = chainOf(workbench, appendDismissedId)?.run?.status === 'DONE';
  const header = headerTick(sel, effectiveRows);
  const onToggle = useCallback((ep: number) => {
    setSel((cur) => {
      const next = toggleTick(cur, ep, effectiveRows);
      const r = effectiveRows.find((x) => x.ep === ep);
      if (next.has(ep) && r?.contentSha) tickSha.current.set(ep, r.contentSha); else tickSha.current.delete(ep);
      return next;
    });
  }, [effectiveRows]);
  const onToggleAll = () => {
    const next = header === 'all' ? new Set<number>() : selectAllTickable(effectiveRows);
    tickSha.current = new Map(effectiveRows.filter((r) => next.has(r.ep) && r.contentSha).map((r) => [r.ep, r.contentSha as string]));
    setSel(next);
  };
  const pushSelected = async () => {
    if (!sticky.visible || batchActions.starting) return;
    if (sticky.partial) {
      const c = partialConfirmOf(sticky.n, tickableEpisodes(effectiveRows).length, previewCounts);
      if (!(await askConfirm({ title: c.title, content: c.content, okText: c.okText }))) return;
    }
    const ok = await batchActions.start(workbench?.episodes ?? [], selected);
    if (ok) { setSel(new Set()); tickSha.current = new Map(); }
  };

  // Bộ lọc nằm ở URL (`rf`) để Back/Về Sổ giữ nguyên. Chưa có `rf` ⇒ mặc định theo số đếm, CHỐT vào URL một lần khi đã dò xong tên gốc (khỏi nhảy tab về sau).
  const rfParam = parseReviewFilter(searchParams.get('rf'));
  const ready = !!workbench && plan.status !== 'loading';
  useEffect(() => {
    if (rfParam || !ready) return;
    setSearchParams((prev) => setReviewFilterParams(prev, defaultFilter(counts)), { replace: true });
  }, [rfParam, ready, counts, setSearchParams]);
  const filter: ReviewFilter = rfParam ?? 'all';
  const visible = useMemo(() => sortRows(filterRows(rows, filter), sort), [rows, filter, sort]);

  const steps = useMemo(() => (workbench ? stepRowItems(workbench) : { items: [], clearText: null }), [workbench]);
  const locked = stageState('script') === 'locked';
  const pushDisabled = busy || scriptJob.starting || !!scriptJob.scriptJob || locked || !hasScripts;
  const fixOffReason = locked ? 'Bước đang khoá' : busy || scriptJob.starting || !!scriptJob.scriptJob ? 'Đang có việc chạy nền — đợi xong rồi thử lại' : null;
  // «Chấm lại bằng AI»: việc nền của workbench do `rejudgeActionOf` tự đọc (kể cả chính job chấm lại); ở đây chỉ phần FE biết riêng (chặng khoá / job của phiên này chưa lên workbench).
  const rejudgeOffReason = locked ? 'Bước đang khoá' : scriptJob.starting || !!scriptJob.scriptJob ? 'Đang có việc chạy nền — đợi xong rồi thử lại' : null;
  const pushTip = workbench?.handoff.readiness === 'blocked' ? friendlyReasonText(workbench.handoff.blockers[0]?.message) : undefined;

  if (!workbench) return <div className="wb-loading"><Spin /></div>;

  const setFilter = (f: ReviewFilter) => setSearchParams((prev) => setReviewFilterParams(prev, f));
  const openRow = (r: ReviewRow, extra: { scene?: string | null; openDropped?: boolean } = {}) => openEpisodeFromReview({ ep: r.ep, filter, ...extra });
  const onMain = (r: ReviewRow) => {
    if (r.button.kind === 'askAssistant') {
      openRow(r, { scene: r.button.scene });
      // KHÔNG autoSend / suggestedText: chỉ tick sẵn lỗi trong khung Trợ lý, người dùng tự quyết gửi.
      requestAssistant({ episode: r.ep, issueIds: r.button.issueIds });
    } else openRow(r);
  };
  // Ô bấm được = như nút «Xem» (mở đúng tập); riêng «Chưa lên phim» mở sẵn khối tình tiết chưa lên phim.
  const cell = (r: ReviewRow, children: React.ReactNode, dropped = false) => (
    <button type="button" className="wb-rb-cell" onClick={() => openRow(r, dropped ? { openDropped: true } : {})}>{children}</button>
  );

  const written = rows.filter((r) => r.written === 'draft').length;
  const emptyText = emptyFilterText(filter, counts);

  return (
    <section className="wb-rb" aria-label="Sổ duyệt">
      <header className="wb-rb-head">
        <div className="wb-rb-title">
          <h2>Sổ duyệt</h2>
          <p>{REVIEW_DESC}</p>
          <p className="wb-rb-kpi" aria-label="Tóm tắt">
            <b>{written}</b> tập đã viết · <b className={counts.need > 0 ? 'wb-rb-need-n' : undefined}>{counts.need}</b> còn cần bạn{counts.hint > 0 ? <> · <b>{counts.hint}</b> gợi ý</> : null} · <b>{counts.pushed}</b> đã đẩy
          </p>
        </div>
        <Space>
          {batch && <Button onClick={batchActions.openResults}>{batchRunning ? 'Xem tiến độ đẩy' : 'Xem kết quả lần đẩy'}</Button>}
          {showReviewHeaderPush(bandHoldsPush) && (
            <Tooltip title={pushTip}>
              <Button loading={handoff.checking} disabled={pushDisabled || batchRunning} onClick={() => { void handoff.click(); }}>Đẩy sang Chapters…</Button>
            </Tooltip>
          )}
        </Space>
      </header>
      {previewBar.line && <div className="wb-rb-preview" role="status">
        <Text type="secondary">{previewBar.line}</Text>
        {previewBar.showRetry && <Button type="link" size="small" onClick={previewState.retry}>Tính lại</Button>}
      </div>}

      <div className="wb-rb-steps" role="group" aria-label="Bước 1–4">
        <div className="wb-rb-steps-h"><Pill tone={steps.clearText ? 'ok' : 'todo'}>Bước 1–4</Pill> {steps.clearText ? <span>{steps.clearText}</span> : <b>Việc dở trước khi viết</b>}</div>
        {steps.items.map((it, i) => (
          <div className="wb-rb-step" key={`${it.stage}-${i}`}>
            <span className="wb-rb-step-n" aria-hidden="true">{['source', 'skeleton', 'localize', 'split'].indexOf(it.stage) + 1}</span>
            <span className="wb-rb-step-t">{friendlyReasonText(it.text)}</span>
            <Pill tone={it.blocker ? 'bad' : it.machine ? 'warn' : 'todo'}>{it.blocker ? 'Còn cần bạn' : it.machine ? MACHINE_SKIPPED_TAG : 'Gợi ý'}</Pill>
            <Button size="small" onClick={() => goTo({ stage: it.stage })} aria-label={`Xem ${STAGE_LABEL[it.stage]}`}>Xem</Button>
          </div>
        ))}
      </div>
      {data.loadError ? <div className="wb-rb-warning-summary wb-rb-warning-error">Không tải được cảnh báo nhẹ <Button size="small" onClick={() => { void refreshAll(); }}>Tải lại</Button></div>
        : summaryGroups.length > 0 && <div className="wb-rb-warning-summary" aria-label="Cảnh báo nhẹ">
          <b>Cảnh báo nhẹ — không chặn đẩy</b>{summaryGroups.map((g) => <span key={g.key}> · <b>{g.count}</b> tập {g.label}</span>)}
          <Button type="link" size="small" onClick={() => setSummaryOpen((v) => !v)}>{summaryOpen ? 'Thu gọn' : 'Xem chi tiết'}</Button>
          {summaryOpen && <div className="wb-rb-warning-detail">
            {summaryGroups.map((g) => <div key={g.key} className="wb-rb-warning-group"><b>{g.label} · {g.count} tập</b>{g.episodes.map((ep) => <Button key={ep} size="small" onClick={() => openEpisodeFromReview({ ep, filter })}>Tập {ep}</Button>)}{g.toggleLabel && <Button type="link" size="small" className="wb-rb-warning-more" aria-expanded={g.expanded} onClick={() => setExpandedGroups((cur) => toggleExpandedGroup(cur, g.key))}>{g.toggleLabel}</Button>}<span className="wb-rb-warning-hint"> Mở từng tập để dùng nút Phương án hiện có.</span></div>)}
          </div>}
        </div>}

      {counts.auto > 0 && (
        <div className="wb-rb-auto">
          <Dot tone="ok" /> <span>{counts.auto} tập máy đã tự sửa, không cần bạn làm gì</span>
          <Button type="link" size="small" onClick={() => setFilter('auto')}>Xem</Button>
          <Button type="link" size="small" onClick={() => setFilter('auto')}>Xem trước/sau · hoàn tác</Button>
        </div>
      )}

      <div className="wb-rb-tabs" role="tablist" aria-label="Lọc tập">
        {TABS.filter((t) => t.key !== 'hint' || counts.hint > 0 || filter === 'hint').map((t) => (
          <button
            key={t.key} type="button" role="tab" aria-selected={filter === t.key} className={`wb-rb-tab${filter === t.key ? ' wb-rb-tab-on' : ''}`}
            onClick={() => setFilter(t.key)}
          >{t.label} <span className="wb-rb-tab-n">{counts[t.key]}</span></button>
        ))}
      </div>

      {visible.length === 0 ? (
        <div className="wb-rb-empty" data-tour="ledger">{emptyText}</div>
      ) : (
        <div className="wb-rb-scroll" data-tour="ledger">
          <table className="wb-rb-table">
            <thead>
              <tr>
                <th className="wb-rb-tick">
                  <Checkbox
                    aria-label="Chọn tất cả tập đạt" checked={header === 'all'} indeterminate={header === 'some'} disabled={header === 'disabled' || batchRunning}
                    onChange={onToggleAll}
                  />
                </th>
                <th>Tập</th>
                <th>Giám khảo AI</th>
                <SortTh label="Lỗi cần xem" tip="Bấm để sắp xếp theo số lỗi cần xem" k="errors" sort={sort} onSort={(k) => setSort((s) => cycleSort(s, k))} />
                <SortTh label="Tên gốc rò" tip="Tên gốc còn rò, tính trước khi đẩy. Bấm để sắp xếp" k="leak" sort={sort} onSort={(k) => setSort((s) => cycleSort(s, k))} />
                <SortTh label="Chưa lên phim" tip="Tình tiết nguyên tác chưa lên phim. Bấm để sắp xếp" k="dropped" sort={sort} onSort={(k) => setSort((s) => cycleSort(s, k))} />
                <th>Chapters</th>
                <th className="wb-rb-act"><span className="wb-rb-sr">Việc</span></th>
              </tr>
            </thead>
            <tbody>
              {visible.map((r) => {
                const effective = effectiveRows.find((x) => x.ep === r.ep) ?? r;
                const overlayItem = overlay.get(r.ep);
                const skipView = overlayItem?.kind === 'notEligible' ? skipRowView(overlayItem.code, overlayItem.reason, r.ep, overlayItem.blockedBy) : null;
                const need = r.groups.includes('need');
                const hintRow = r.groups.includes('hint');
                // 261023 E3-P3b1: thẻ «Máy đã tự sửa» nằm dưới hàng ở tab «Máy đã tự sửa»; tập đã hoàn tác hiện ở mọi tab (kèm nút Dùng lại).
                const showFix = !!r.fix && (filter === 'auto' || r.fix.state === 'undone');
                const wbEpRow = workbench.episodes.find((e) => e.ep === r.ep);
                const scriptRow = scripts.find((x) => x.episode === r.ep);
                return (
                  <React.Fragment key={r.ep}>
                  <tr className={need ? 'wb-rb-row-need' : hintRow ? 'wb-rb-row-hint' : undefined} data-ep={r.ep}>
                    <td className="wb-rb-tick">
                      {(() => {
                        const why = batchRunning ? 'Đang đẩy ở nền — chờ xong' : tickOffReason(effective);
                        const box = <Checkbox aria-label={`Chọn tập ${r.ep}`} checked={sel.has(r.ep)} disabled={!canTick(effective) || batchRunning} onChange={() => onToggle(r.ep)} />;
                        return why ? <Tooltip title={why}><span>{box}</span></Tooltip> : box;
                      })()}
                    </td>
                    <td className="wb-rb-ep">
                      <button type="button" className="wb-rb-eplink" onClick={() => openRow(r)}><b>{r.ep}.</b> {r.title || `Tập ${r.ep}`}</button>
                      {r.written === 'none' && <span className="wb-rb-sub"> chưa viết</span>}
                      {r.written === 'failed' && <span className="wb-rb-sub wb-rb-sub-bad"> viết hỏng</span>}
                      {r.trace && (
                        <LocalizeTraceNote
                          line={r.trace.line} testId="review-row-trace"
                          onOpenStage={() => goTo({ stage: 'localize' })}
                          onOpenScene={r.trace.scene ? () => openRow(r, { scene: r.trace!.scene }) : undefined}
                        />
                      )}
                      {r.notes.map((n, i) => (
                        <div key={i} className={`wb-rb-note ${n.sign === '✗' ? 'wb-rb-note-bad' : 'wb-rb-note-warn'}`}>
                          {n.sign === '✗' ? <span className="wb-rb-x" aria-hidden="true">✗</span> : <Bang tone="warn" />} <span>{n.text}</span>
                        </div>
                      ))}
                      {overlayItem && <div className="wb-rb-skip">
                        <span>{overlayItem.reason}{overlayItem.kind === 'notEligible' && overlayItem.code === 'SOURCE_NAME_UNRESOLVED' && plan.status === 'ok' ? sourceNameSuffix(plan.plan.sourceNameBlock?.find((x) => x.episode === r.ep)?.terms) : ''}</span>
                        {overlayItem.kind === 'notEligible'
                          ? <Button size="small" onClick={() => openEpisodeFromReview({ ep: skipView!.button.episode, filter })}>{skipView!.button.label}</Button>
                          : <Button size="small" onClick={() => { void refreshAll(); }}>Tải lại</Button>}
                      </div>}
                    </td>
                    <td>{r.judge ? cell(r, <Pill tone={PILL_TONE[r.judge.tone]} title={r.judge.tip}>{r.judge.label}</Pill>) : <Dash />}</td>
                    <td>
                      {r.errors && r.errors.count > 0
                        ? cell(r, <><Pill tone="warn">{r.errors.count} lỗi</Pill>{r.errors.scenesText && <span className="wb-rb-sub"> {r.errors.scenesText}</span>}</>)
                        : <Dash />}
                    </td>
                    <td>
                      {!r.leak ? <Dash /> : r.leak.kind === 'unknown'
                        ? <Tooltip title={r.leak.tip}><span><Pill tone="todo">Chưa rõ</Pill></span></Tooltip>
                        : r.leak.kind === 'block'
                          ? cell(r, <><Pill tone="bad" title={r.leak.tip}>{r.leak.count} tên</Pill><span className="wb-rb-sub"> sẽ chặn đẩy</span></>)
                          : r.leak.kind === 'swap'
                            ? <Tooltip title={r.leak.tip}><span className="wb-rb-sub">máy sẽ tự thay {r.leak.replaced}</span></Tooltip>
                            : r.leak.kind === 'warn'
                              ? cell(r, <><Pill tone="warn" title={r.leak.tip}>{r.leak.count} tên</Pill><span className="wb-rb-sub"> cảnh báo</span></>)
                              : <span>0</span>}
                    </td>
                    <td>
                      {!r.dropped ? <Dash /> : (r.dropped.important + r.dropped.minor) === 0
                        ? <span>0</span>
                        : cell(r, <span>{r.dropped.important} quan trọng · {r.dropped.minor} phụ</span>, true)}
                    </td>
                    <td><Pill tone={PILL_TONE[r.chapters.tone]} title={r.chapters.tip}>{r.chapters.label}</Pill></td>
                    <td className="wb-rb-act">
                      <div className="wb-rb-actions">
                        {scriptRow && (
                          <RejudgeButton
                            storyId={storyId} episode={r.ep} script={scriptRow} refreshAll={refreshAll}
                            action={rejudgeActionOf({ ep: wbEpRow, script: scriptRow, workbench, lockedReason: rejudgeOffReason })}
                          />
                        )}
                        <Button size="small" onClick={() => onMain(r)}>{r.button.label}</Button>
                      </div>
                    </td>
                  </tr>
                  {showFix && r.fix && wbEpRow && scriptRow && (
                    <tr className="wb-rb-fixrow" data-ep-fix={r.ep}>
                      <td colSpan={8}>
                        <AutoFixCardView
                          card={r.fix} wbEp={wbEpRow} script={scriptRow} outline={data.record?.outline ?? null} storyId={storyId} refreshAll={refreshAll}
                          disabledReason={fixOffReason}
                        />
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <Text type="secondary" className="wb-rb-foot">{reviewFootText(showReviewHeaderPush(bandHoldsPush))}</Text>

      {sticky.visible && (
        <div className="wb-rb-sticky" role="region" aria-label="Đẩy các tập đã chọn">
          <Button type="primary" cta={stickyCta} loading={batchActions.starting} disabled={batchRunning} onClick={() => { void pushSelected(); }}>{sticky.label}</Button>
          <Button type="link" onClick={() => { setSel(new Set()); tickSha.current = new Map(); }}>Bỏ chọn</Button>
        </div>
      )}
      <HandoffBatchResults batch={batch} open={batchActions.resultsOpen} onClose={batchActions.closeResults} onCancel={(id) => { void batchActions.cancel(id); }} />

      <HandoffModal plan={handoff.modalPlan} onClose={handoff.closeModal} />
    </section>
  );
};

export default ReviewBook;
